// ¿Hay que estar quieto? Una pregunta, una respuesta, un sitio.
//
// POR QUÉ EXISTE, Y ES UN CASO REAL DEL 2 DE SEPTIEMBRE DE 2026. El ajuste «Movimiento» tiene
// tres opciones —**Como el sistema**, **Completo** y **Reducido**— y estaba conectado a medias
// en las dos direcciones, que es la peor forma de estarlo:
//
//   · SEIS sitios preguntaban `prefers-reduced-motion` al navegador y solo UNO miraba además el
//     ajuste. Así que quien tiene el sistema en «reducir» y elige **Completo** seguía viéndolo
//     todo quieto: el ajuste no hacía nada, y precisamente para quien lo eligió a propósito.
//   · Y al revés: quien eligió **Reducido** con el sistema sin pedir nada paraba el CSS —hay
//     una regla que apaga todas las animaciones— pero no paraba el mapa del Recorrido, ni la
//     pantalla de reconexión, ni la inercia del cursor, ni el cuarto del mentor, porque esos
//     cuatro preguntaban solo al navegador.
//
// No daba ningún error. Se descubre eligiendo una opción y viendo que no pasa nada, que es lo
// que hace un alumno una vez antes de dejar de fiarse de la pantalla entera.
//
// LA REGLA, Y ES UNA FRASE: **manda el alumno, y solo si dice «como el sistema» se le pregunta
// al sistema.** Ya estaba escrita en un sitio y faltaba en otros cinco; ahora vive aquí y la
// usan todos.
//
// AQUÍ ESTÁ LA ÚNICA LLAMADA A `matchMedia` DE TODA LA CASA, y eso lo vigila
// `tools/check_movimiento.py`. Una pregunta que se puede hacer en seis sitios se contesta de
// seis maneras en cuanto uno de los seis se olvide de la mitad —que es exactamente lo que pasó.

/**
 * Lo único que se mueve cuando toca estar quieto: un fundido de 150 ms al aparecer.
 *
 * LO DECIDIÓ GABRI EL 7 DE SEPTIEMBRE DE 2026, y la razón es que cero absoluto tampoco estaba
 * bien: una pantalla que tapa entera y aparece de golpe, sin nada, se lee como un fallo de
 * pintado. **Un fundido de opacidad no es movimiento**: no hay nada que se desplace ni que
 * crezca, que es lo que marea, y las guías de accesibilidad lo aceptan justo por eso.
 *
 * CIENTO CINCUENTA MILISEGUNDOS Y NO MÁS: por debajo no se percibe y por encima ya es una
 * animación. El número vive aquí, con la regla, porque el día que lo use una segunda pantalla
 * tienen que fundir las dos igual.
 */
import { lee as leePreferencias } from "./preferencias.mjs";

export const FUNDIDO_QUIETO = 0.15;

export const COMO_EL_SISTEMA = "sistema";
export const COMPLETO = "completo";
export const REDUCIDO = "reducido";

/**
 * La regla. Pura: recibe lo que dice el alumno y lo que dice el aparato.
 *
 * `completo` NO es «no hacer nada»: es la orden de ignorar al sistema. Sin ese caso escrito,
 * la opción sobra de la pantalla.
 */
export function hayQueEstarQuieto(prefs = {}, sistemaPide = false) {
  if (prefs.movimiento === REDUCIDO) return true;
  if (prefs.movimiento === COMPLETO) return false;
  return !!sistemaPide;               // «como el sistema», que es lo que viene por defecto
}

/** Lo que dice el aparato. Es la única puerta al navegador, y por eso no lleva regla dentro. */
export function sistemaPideMenos() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * EL MISMO AJUSTE, PARA LO QUE NO ES REACT: el mundo 3D. Las escenas no tienen hooks —dibujan
 * sesenta veces por segundo, muy lejos de cualquier estado— y hasta hoy el ajuste de movimiento
 * no llegaba a ellas: quien pedía menos movimiento seguía viendo el aro del Ojo girando y la
 * esfera de Hipatia dando vueltas. Es la misma avería que este fichero vino a arreglar, vista
 * desde el otro lado.
 *
 * SE PREGUNTA POCO, a propósito. Leer `localStorage` en cada cuadro es de las cosas que no dan
 * error y ponen la página espesa —lo dice `explanada/minijuego.js` con las mismas palabras—, así
 * que la respuesta se guarda `CADA` milisegundos y entre medias se devuelve la de antes.
 *
 * Devuelve una función: `quieto()`. Sin ventana —en node, en las pruebas— siempre es `false`:
 * lo que no tiene pantalla no tiene movimiento que reducir.
 */
export const CADA = 500;
export function creaVigilanteDeMovimiento(ventana = typeof window === "undefined" ? null : window,
  ahora = () => Date.now()) {
  let cuando = -Infinity;
  let dice = false;
  return function quieto() {
    if (!ventana) return false;
    const t = ahora();
    if (t - cuando < CADA) return dice;
    cuando = t;
    let prefs = {};
    try { prefs = leePreferencias(ventana.localStorage) || {}; } catch { prefs = {}; }
    let sistema = false;
    try {
      sistema = !!ventana.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    } catch { sistema = false; }
    dice = hayQueEstarQuieto(prefs, sistema);
    return dice;
  };
}

/** Y avisa si el aparato cambia de idea sin recargar. Devuelve cómo dejar de escuchar. */
export function alCambiarElSistema(avisa) {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
  const oye = () => avisa(mq.matches);
  mq.addEventListener("change", oye);
  return () => mq.removeEventListener("change", oye);
}
